import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Edit2, Check, X } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Note {
  id: string;
  note: string;
  created_at: string;
  updated_at: string;
}

interface LoanNotesProps {
  loanId: string;
  clientName: string;
}

export function LoanNotes({ loanId, clientName }: LoanNotesProps) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNote, setNewNote] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadNotes();
  }, [loanId]);

  const loadNotes = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("loan_notes")
      .select("*")
      .eq("loan_id", loanId)
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Erro ao carregar anotações");
      setIsLoading(false);
      return;
    }

    setNotes(data || []);
    setIsLoading(false);
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Digite uma anotação");
      return;
    }

    setIsSaving(true);
    const { data: userData } = await supabase.auth.getUser();
    
    if (!userData.user) {
      toast.error("Usuário não autenticado");
      setIsSaving(false);
      return;
    }

    const { error } = await supabase
      .from("loan_notes")
      .insert({
        loan_id: loanId,
        user_id: userData.user.id,
        note: newNote.trim(),
      });

    if (error) {
      toast.error("Erro ao adicionar anotação");
      setIsSaving(false);
      return;
    }

    toast.success("Anotação adicionada!");
    setNewNote("");
    setIsSaving(false);
    loadNotes();
  };

  const handleUpdateNote = async (id: string) => {
    if (!editingText.trim()) {
      toast.error("Digite uma anotação");
      return;
    }

    const { error } = await supabase
      .from("loan_notes")
      .update({ note: editingText.trim() })
      .eq("id", id);

    if (error) {
      toast.error("Erro ao atualizar anotação");
      return;
    }

    toast.success("Anotação atualizada!");
    setEditingId(null);
    setEditingText("");
    loadNotes();
  };

  const handleDeleteNote = async (id: string) => {
    const { error } = await supabase
      .from("loan_notes")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error("Erro ao deletar anotação");
      return;
    }

    toast.success("Anotação removida!");
    loadNotes();
  };

  const startEdit = (note: Note) => {
    setEditingId(note.id);
    setEditingText(note.note);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center gap-2 pb-3 border-b">
        <FileText className="h-5 w-5 text-primary" />
        <h4 className="font-semibold">Anotações sobre {clientName}</h4>
      </div>

      {/* Nova Anotação */}
      <div className="space-y-2">
        <Textarea
          placeholder="Escreva uma anotação sobre este cliente..."
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          rows={3}
          className="resize-none"
        />
        <Button
          onClick={handleAddNote}
          disabled={isSaving || !newNote.trim()}
          className="w-full gap-2"
        >
          <Plus className="h-4 w-4" />
          Adicionar Anotação
        </Button>
      </div>

      {/* Lista de Anotações */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {isLoading ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Carregando anotações...
          </p>
        ) : notes.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Nenhuma anotação ainda. Adicione a primeira!
          </p>
        ) : (
          notes.map((note) => (
            <div
              key={note.id}
              className="p-3 rounded-lg border bg-card space-y-2"
            >
              {editingId === note.id ? (
                <div className="space-y-2">
                  <Textarea
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    rows={3}
                    className="resize-none"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleUpdateNote(note.id)}
                      className="gap-1"
                    >
                      <Check className="h-3 w-3" />
                      Salvar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={cancelEdit}
                      className="gap-1"
                    >
                      <X className="h-3 w-3" />
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(note.created_at), "dd/MM/yyyy 'às' HH:mm", {
                        locale: ptBR,
                      })}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEdit(note)}
                        className="h-7 w-7 p-0"
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remover anotação?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta ação não pode ser desfeita. A anotação será removida permanentemente.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteNote(note.id)}>
                              Remover
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
